'use client';

import { useEffect, useRef, useState } from 'react';

import { useTranslations } from 'next-intl';

import { Stdv1Stat } from '../observability/constants';
import { recordMetric } from '../observability/metrics';

import {
  EBAY_POPUP_POLL_INTERVAL_MS,
  EBAY_SSO_MESSAGE_TYPE,
  EBAY_SSO_PENDING_RESULT_STORAGE_KEY,
  EBAY_SSO_STATE_STORAGE_KEY,
} from './constants';
import { openEbayPopup } from './ebayPopup';
import {
  type EbayPendingResult,
  type EbayStateRecord,
  readEbayStorageRecord,
} from './ebayStorage';

import { fetchEbayAuthorizationUrl } from '@/modules/login/api';
import { removeLocalStorageItem, setLocalStorageItem } from '@/modules/storage';

export type EbayAuthError =
  'popupBlocked' | 'authorizeFailed' | 'stateMismatch' | 'noCode';

type EbayResult = {
  code?: string | null;
  state?: string | null;
};

type EbayCallbackMessage = EbayResult & {
  type: typeof EBAY_SSO_MESSAGE_TYPE;
};

type Props = {
  onEbayAuthorized: (code: string) => Promise<void> | void;
  onError: (error: EbayAuthError) => void;
};

export function useEbayAuth({ onEbayAuthorized, onError }: Props) {
  const t = useTranslations('common');
  const [isInProgress, setIsInProgress] = useState(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  async function handleEbayResult({ code, state: returnedState }: EbayResult) {
    const storedState = readEbayStorageRecord<EbayStateRecord>(
      EBAY_SSO_STATE_STORAGE_KEY
    )?.state;
    if (!code) {
      setIsInProgress(false);
      return onError('noCode');
    }

    // CSRF protection to ensure the result belongs to the flow this browser started
    if (!returnedState || returnedState !== storedState) {
      setIsInProgress(false);
      recordMetric({
        type: 'increment',
        stat: Stdv1Stat['ebay.auth.state_mismatch_error'],
      });
      return onError('stateMismatch');
    }

    // Only remove on a match: a rejected or cancelled result must not wipe another tab's newer flow (unused records expire via the TTL)
    removeLocalStorageItem(EBAY_SSO_STATE_STORAGE_KEY);

    try {
      await onEbayAuthorized(code);
    } finally {
      setIsInProgress(false);
    }
  }

  useEffect(() => {
    // The callback stashes the result when it opens without an opener, e.g. after the Android app bounces it to a new tab
    const pending = readEbayStorageRecord<EbayPendingResult>(
      EBAY_SSO_PENDING_RESULT_STORAGE_KEY
    );
    removeLocalStorageItem(EBAY_SSO_PENDING_RESULT_STORAGE_KEY);

    if (pending) {
      recordMetric({
        type: 'increment',
        stat: Stdv1Stat['ebay.auth.no_opener_resume'],
      });
      setIsInProgress(true);
      handleEbayResult(pending);
    }

    return () => cleanupRef.current?.();
  }, []);

  function startPopupFlow() {
    cleanupRef.current?.();

    const popup = openEbayPopup(t('EbaySSO.PopupTitle'));

    if (!popup) {
      return onError('popupBlocked');
    }

    const state = crypto.randomUUID();
    // localStorage, not sessionStorage: the callback may land in a new tab
    const stateRecord: EbayStateRecord = {
      state,
      returnTo: window.location.pathname + window.location.search,
      createdAt: Date.now(),
    };
    setLocalStorageItem(
      EBAY_SSO_STATE_STORAGE_KEY,
      JSON.stringify(stateRecord)
    );
    setIsInProgress(true);

    const controller = new AbortController();
    let pollTimer: number | undefined;

    function cleanup() {
      controller.abort();
      window.clearInterval(pollTimer);
      // The stored state is kept on purpose. On Android the Depop app can reopen the eBay callback
      // in a new browser tab that has no window.opener (so it can't postMessage back). That tab
      // reads this state later, after this popup may already be closed. handleEbayResult clears it.
      popup?.close();
      cleanupRef.current = null;
    }

    // Clean up the setup if the popup is closed
    pollTimer = window.setInterval(() => {
      if (popup.closed) {
        cleanup();
        setIsInProgress(false);
        recordMetric({
          type: 'increment',
          stat: Stdv1Stat['ebay.auth.popup_closed_by_user_error'],
        });
      }
    }, EBAY_POPUP_POLL_INTERVAL_MS);

    function handleMessage(event: MessageEvent<EbayCallbackMessage>) {
      if (
        event.origin !== window.location.origin ||
        event.source !== popup ||
        event.data?.type !== EBAY_SSO_MESSAGE_TYPE
      ) {
        return;
      }

      cleanup();
      handleEbayResult(event.data);
    }

    window.addEventListener('message', handleMessage, {
      signal: controller.signal,
    });
    cleanupRef.current = cleanup;

    fetchEbayAuthorizationUrl(state)
      .then(({ data }) => {
        if (!popup.closed) {
          // Redirect to the eBay authorization URL
          popup.location.href = data.authorization_url;
        }
      })
      .catch(() => {
        if (controller.signal.aborted) {
          return;
        }
        cleanup();
        setIsInProgress(false);
        onError('authorizeFailed');
        recordMetric({
          type: 'increment',
          stat: Stdv1Stat['ebay.auth.authorize_error'],
        });
      });
  }

  return { startPopupFlow, isInProgress };
}
